import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { Link } from 'react-scroll';
import { useMember } from '../contexts/MemberContext';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isMember } = useMember();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route changes to avoid overlaying content
  useEffect(() => {
    if (isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
  }, [location.pathname]);


  const navItems = [
    { name: 'Home', to: '/', type: 'route' },
    { name: 'My Story', to: '/about', type: 'route' },
    { name: 'Services', to: '/services', type: 'route' },
    // { name: 'Shop', to: '/shop', type: 'route' }, // Archived - add back later
    { name: 'Contact', to: '/contact', type: 'route' },
    // Login: send logged-in members to dashboard, others to login
    { name: 'Login', to: isMember ? '/dashboard' : '/login', type: 'route' },
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed w-full z-50 transition-all duration-300 ${
        isScrolled ? 'backdrop-blur-md shadow-soft-lg' : 'backdrop-blur-sm'
      }`}
      style={{ backgroundColor: isScrolled ? '#d9a79c' : 'rgba(217, 167, 156, 0.9)' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-16">
          {/* Logo */}
          {location.pathname === '/' ? (
            <Link to="hero" smooth={true} duration={500} className="flex-shrink-0 flex items-center">
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="cursor-pointer flex items-center"
              >
                <img 
                  src="/images/Aleracurrent_charcoaltrans.png" 
                  alt="Alera" 
                  className="h-16 md:h-24 w-auto"
                  style={{ objectFit: 'contain', filter: 'brightness(0) invert(1)' }}
                />
              </motion.div>
            </Link>
          ) : (
            <RouterLink to="/" className="flex-shrink-0 flex items-center">
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="cursor-pointer flex items-center"
              >
                <img 
                  src="/images/Aleracurrent_charcoaltrans.png" 
                  alt="Alera" 
                  className="h-16 md:h-24 w-auto"
                  style={{ objectFit: 'contain', filter: 'brightness(0) invert(1)' }}
                />
              </motion.div>
            </RouterLink>
          )}

          {/* Right side: Desktop Navigation + Notifications/Mobile toggle */}
          <div className="ml-auto flex items-center">
            {/* Desktop Navigation */}
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-8">
                {navItems.map((item) => (
                  item.type === 'route' ? (
                    <RouterLink
                      key={item.name}
                      to={item.to}
                      className="cursor-pointer px-3 py-2 text-sm font-medium transition-colors duration-200 text-white"
                      onMouseEnter={(e) => e.currentTarget.style.color = '#3a3a3a'}
                      onMouseLeave={(e) => e.currentTarget.style.color = ''}
                    >
                      {item.name}
                    </RouterLink>
                  ) : (
                    <Link
                      key={item.name}
                      to={item.to}
                      smooth={true}
                      duration={500}
                      className="cursor-pointer px-3 py-2 text-sm font-medium transition-colors duration-200 text-white"
                      onMouseEnter={(e) => e.currentTarget.style.color = '#3a3a3a'}
                      onMouseLeave={(e) => e.currentTarget.style.color = ''}
                    >
                      {item.name}
                    </Link>
                  )
                ))}
              </div>
            </div>

            {/* Mobile menu */}
            <div className="flex items-center space-x-2 ml-4">
              {/* Mobile menu button */}
              <div className="md:hidden">
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="p-2 rounded-md text-white"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden rounded-lg shadow-soft-lg mt-2 p-4"
            style={{ backgroundColor: '#d9a79c' }}
          >
            {navItems.map((item) => (
              item.type === 'route' ? (
                <RouterLink
                  key={item.name}
                  to={item.to}
                  className="block px-3 py-2 text-white cursor-pointer"
                  onMouseEnter={(e) => e.currentTarget.style.color = '#3a3a3a'}
                  onMouseLeave={(e) => e.currentTarget.style.color = ''}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.name}
                </RouterLink>
              ) : (
                <Link
                  key={item.name}
                  to={item.to}
                  smooth={true}
                  duration={500}
                  className="block px-3 py-2 text-white cursor-pointer"
                  onMouseEnter={(e) => e.currentTarget.style.color = '#3a3a3a'}
                  onMouseLeave={(e) => e.currentTarget.style.color = ''}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              )
            ))}
          </motion.div>
        )}
      </div>
    </motion.nav>
  );
};

export default Navbar;
